import { useState } from "react";
import { Plus, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/api";
import { Panel, StatusBadge } from "./common";
import { DataTable } from "./DataTable";
import { FormDialog } from "./FormDialog";
import { FileBox } from "./Files";
import { amt } from "./Expenses";

export const DEPOSIT_TYPES = ["Client advance", "Client payment", "Security deposit (paid)"];
const isClient = (d) => d.type !== "Security deposit (paid)";
const live = (e) => e.approval_status !== "Rejected";
const sum = (l, f = amt) => l.reduce((s, x) => s + f(x), 0);

export function computePL(p, costItems, vendor, internal, deposits) {
  const contract = Number(p.client_value || 0);
  const budget = Number(p.budget || 0);
  const estimate = sum(costItems, (c) => Number(c.estimate || 0));
  const vendorCost = sum(vendor.filter(live));
  const internalCost = sum(internal.filter(live));
  const totalCost = vendorCost + internalCost;
  const received = sum(deposits.filter((d) => isClient(d) && d.status === "Received"), (d) => Number(d.amount || 0));
  const security = sum(deposits.filter((d) => !isClient(d) && d.status === "Paid"), (d) => Number(d.amount || 0));
  const paidOut = sum([...vendor, ...internal].filter((e) => e.payment_status === "Paid"));
  const payable = sum([...vendor, ...internal].filter((e) => live(e) && e.payment_status !== "Paid"));
  return {
    contract, budget, estimate, vendorCost, internalCost, totalCost, received, security, paidOut, payable,
    estProfit: contract - estimate, profit: contract - totalCost, margin: contract ? ((contract - totalCost) / contract) * 100 : 0,
    remainingBudget: budget - totalCost, receivable: Math.max(contract - received, 0), netCash: received - paidOut - security,
  };
}

function Line({ label, value, strong, neg, testid, sub }) {
  return (
    <div className={`flex items-center justify-between py-2 ${strong ? "border-t border-zinc-200 pt-3 font-bold text-zinc-900" : "text-sm text-zinc-600"}`}>
      <span className={sub ? "pl-4" : ""}>{label}</span>
      <span data-testid={testid} className={`whitespace-nowrap ${neg ? "text-rose-600" : ""}`}>{neg && value > 0 ? `− ${inr(value)}` : inr(value)}</span>
    </div>
  );
}

export function PLStatement({ pl, onEditContract }) {
  const up = pl.profit >= 0;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel testid="pl-statement-panel" title="Project P&L" action={onEditContract && <Button data-testid="edit-contract-value-button" size="sm" variant="outline" onClick={onEditContract}>Edit contract value</Button>}>
        <Line label="Client contract value" value={pl.contract} testid="pl-contract" />
        <Line label="Vendor expenses" value={pl.vendorCost} neg sub testid="pl-vendor" />
        <Line label="Internal expenses" value={pl.internalCost} neg sub testid="pl-internal" />
        <Line label="Total cost" value={pl.totalCost} testid="pl-total-cost" />
        <Line label={up ? "Gross profit" : "Gross loss"} value={pl.profit} strong testid="pl-profit" />
        <div className="mt-3 flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
          <span className="flex items-center gap-2 text-sm text-zinc-600">{up ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-rose-600" />}Margin</span>
          <span data-testid="pl-margin" className={`text-lg font-bold ${up ? "text-emerald-600" : "text-rose-600"}`}>{pl.contract ? `${pl.margin.toFixed(1)}%` : "Set contract value"}</span>
        </div>
        <div className="mt-3 text-xs text-zinc-400">Pre-event estimate (contract − cost sheet {inr(pl.estimate)}): <span data-testid="pl-estimated-profit" className="font-semibold text-zinc-600">{inr(pl.estProfit)}</span>. Rejected bills are excluded.</div>
      </Panel>
      <Panel testid="pl-cash-panel" title="Cash position">
        <Line label="Client deposits received" value={pl.received} testid="pl-received" />
        <Line label="Paid to vendors & staff" value={pl.paidOut} neg sub testid="pl-paid-out" />
        <Line label="Security deposits held" value={pl.security} neg sub testid="pl-security" />
        <Line label="Net cash position" value={pl.netCash} strong testid="pl-net-cash" />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-amber-50 px-4 py-3"><div className="text-xs text-amber-700">Receivable from client</div><div data-testid="pl-receivable" className="text-base font-bold text-zinc-900">{inr(pl.receivable)}</div></div>
          <div className="rounded-xl bg-violet-50 px-4 py-3"><div className="text-xs text-violet-700">Payable (unpaid bills)</div><div data-testid="pl-payable" className="text-base font-bold text-zinc-900">{inr(pl.payable)}</div></div>
        </div>
      </Panel>
    </div>
  );
}

export function ExpenseSummaryPanel({ kind, items }) {
  const vendor = kind === "vendor";
  const valid = items.filter(live);
  const stat = (l, v, tone = "text-zinc-900") => <div className="rounded-xl bg-zinc-50 px-3 py-2.5"><div className="text-[11px] text-zinc-500">{l}</div><div className={`text-sm font-bold ${tone}`}>{inr(v)}</div></div>;
  return (
    <Panel testid={`budget-${kind}-panel`} title={vendor ? "Vendor expenses" : "Internal expenses"} action={<a href={vendor ? "/vendor-expenses" : "/internal-expenses"} className="text-xs font-semibold text-brand">Manage →</a>}>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stat("Total (excl. rejected)", sum(valid))}
        {stat("Pending approval", sum(items.filter((e) => (e.approval_status || "Pending") === "Pending")), "text-amber-600")}
        {stat("Approved, unpaid", sum(items.filter((e) => e.approval_status === "Approved" && e.payment_status !== "Paid")), "text-violet-700")}
        {stat("Paid", sum(items.filter((e) => e.payment_status === "Paid")), "text-emerald-600")}
      </div>
      <DataTable testid={`budget-${kind}-table`} rows={items} columns={[
        { key: "who", label: vendor ? "Vendor" : "Employee", render: (e) => <div><div className="font-semibold text-zinc-900">{vendor ? e.company : e.employee}</div><div className="text-xs text-zinc-500">{e.category}</div></div> },
        { key: "amount", label: "Amount", render: (e) => <span className="whitespace-nowrap font-semibold">{inr(amt(e))}</span> },
        { key: "approval_status", label: "Approval", render: (e) => <StatusBadge value={e.approval_status} /> },
        { key: "payment_status", label: "Payment", render: (e) => <StatusBadge value={e.payment_status} /> },
        { key: "files", label: vendor ? "Invoice" : "Receipt", render: (e) => <div className="max-w-[150px]"><FileBox compact testid={`budget-${kind}-files-${e.id}`} entityType={vendor ? "vendor_expense" : "internal_expense"} entityId={e.id} canUpload={false} /></div> },
      ]} />
    </Panel>
  );
}

export function DepositsPanel({ projectId, deposits }) {
  const { can } = useAuth();
  const [form, setForm] = useState(null);
  const { items, create, update } = deposits;
  const save = async (f) => {
    const sec = f.type === "Security deposit (paid)";
    const body = { ...f, status: sec ? (["Paid", "Refunded"].includes(f.status) ? f.status : "Paid") : (["Expected", "Received"].includes(f.status) ? f.status : "Received") };
    return !!(body.id ? await update(body.id, body, "Deposit updated") : await create({ ...body, project_id: projectId }, "Deposit added"));
  };
  return (
    <Panel testid="deposits-panel" title="Deposits & client payments" action={can("Budget", "create") && <Button data-testid="add-deposit-button" size="sm" onClick={() => setForm({ type: "Client advance", status: "Received", date: new Date().toISOString().slice(0, 10) })}><Plus className="h-3.5 w-3.5 mr-1" />Add deposit</Button>}>
      <DataTable testid="deposits-table" rows={items} onRowClick={(d) => can("Budget", "edit") && setForm(d)} columns={[
        { key: "type", label: "Type", render: (d) => <span className="font-semibold text-zinc-900">{d.type}</span> },
        { key: "party", label: "From / to" },
        { key: "amount", label: "Amount", render: (d) => <span className={`whitespace-nowrap font-semibold ${isClient(d) ? "text-emerald-600" : "text-zinc-800"}`}>{isClient(d) ? "+ " : "− "}{inr(d.amount)}</span> },
        { key: "date", label: "Date" },
        { key: "mode", label: "Mode / ref", render: (d) => <span className="text-xs">{[d.mode, d.reference].filter(Boolean).join(" · ") || "—"}</span> },
        { key: "status", label: "Status", render: (d) => <StatusBadge testid={`deposit-status-${d.id}`} value={d.status} /> },
        { key: "proof", label: "Proof", render: (d) => <div onClick={(e) => e.stopPropagation()} className="max-w-[160px]"><FileBox compact testid={`deposit-files-${d.id}`} entityType="deposit" entityId={d.id} canUpload={can("Budget", "edit")} /></div> },
      ]} />
      <FormDialog testid="deposit-form" open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Edit deposit" : "Add deposit"} initial={form} onSubmit={save}
        fields={[{ name: "type", label: "Type", type: "select", options: DEPOSIT_TYPES, required: true }, { name: "party", label: "Client / venue / party", required: true },
          { name: "amount", label: "Amount (₹)", type: "number", required: true }, { name: "date", label: "Date", type: "date", required: true },
          { name: "mode", label: "Mode", type: "select", options: ["Bank transfer", "UPI", "Cheque", "Cash"] }, { name: "reference", label: "Reference (UTR / cheque no.)" },
          { name: "status", label: "Status", type: "select", options: form?.type === "Security deposit (paid)" ? ["Paid", "Refunded"] : ["Expected", "Received"], required: true },
          { name: "remarks", label: "Remarks", type: "textarea" }]} />
    </Panel>
  );
}
